import { useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle, RefreshCw, Server } from 'lucide-react'
import { fetchIbkrChartSnapshot } from '../../lib/marketData/ibkrChartAdapter'
import { getMarketDataStatus, reportMarketDataStatus, useMarketDataStatus } from '../../lib/marketDataStatusEvents'
import { useMarketDataOperations } from '../../hooks/useMarketDataOperations'

function formatDiagnosticTime(value) {
  const date = new Date(value)
  if (!value || Number.isNaN(date.getTime())) return 'Time unavailable'
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  })
}

function diagnosticBadge(classification) {
  if (classification?.status === 'expected') {
    return { label: 'Expected disconnect', className: 'border-forest/30 bg-forest/10 text-forest' }
  }
  if (classification?.status === 'unexpected') {
    return { label: 'Unexpected disconnect', className: 'border-ember/30 bg-ember/10 text-ember' }
  }
  return { label: 'Cause not confirmed', className: 'border-border bg-canvas2 text-inkMuted' }
}

function gatewayConnectionLabel(state, recoveryPhase) {
  if (recoveryPhase) return 'Authenticating'
  if (['ready', 'live', 'stale', 'delayed'].includes(state)) return 'Connected'
  if (['connecting', 'reconnecting'].includes(state)) return 'Reconnecting'
  if (state === 'unentitled') return 'Competing session'
  return 'Unavailable'
}

function displayValue(value, fallback = 'Not reported') {
  return value === null || value === undefined || value === '' ? fallback : value
}

function formatRestartTime(value, timezone) {
  const match = /^(\d{2}):(\d{2})$/.exec(value || '')
  if (!match) return displayValue(value)
  const hour = Number(match[1])
  const minute = match[2]
  const period = hour >= 12 ? 'PM' : 'AM'
  const normalized = hour % 12 || 12
  const zone = timezone === 'America/New_York' ? 'ET' : timezone
  return `${normalized}:${minute} ${period}${zone ? ` ${zone}` : ''}`
}

function GatewaySetting({ label, value, setting }) {
  return (
    <div className="rounded-md border border-border bg-canvas px-3 py-2.5">
      <dt className="font-mono text-[9px] uppercase tracking-wide text-inkMuted">{label}</dt>
      <dd data-setting={setting} className="mt-1 text-sm font-semibold text-ink">{displayValue(value)}</dd>
    </div>
  )
}

export default function MarketDataOperations() {
  const { status, refresh, restart, busyAction, refreshing } = useMarketDataOperations()
  const chartStatus = useMarketDataStatus()
  const chartFailure = chartStatus?.source === 'chart' && !chartStatus.operational
  const [checkingChart, setCheckingChart] = useState(false)
  const [chartCheckMessage, setChartCheckMessage] = useState(null)
  const checkChart = async () => {
    if (!chartStatus?.request || checkingChart) return
    const failed = chartStatus
    setCheckingChart(true)
    setChartCheckMessage('Checking the failed chart request…')
    try {
      const result = await fetchIbkrChartSnapshot(failed.request)
      if (!result.bars.length) throw new Error('No chart bars returned')
      if (getMarketDataStatus() === failed) {
        reportMarketDataStatus({ ...failed, operational: true, state: 'ready', message: `${failed.symbol} chart data loaded successfully.` })
      }
      setChartCheckMessage(`${failed.symbol} chart data received. Quote freshness is checked separately.`)
    } catch (error) {
      setChartCheckMessage(error?.code === 'CHART_AUTH' ? 'Sign in again to load chart data.' : error?.code === 'CHART_EMPTY' ? 'IBKR returned no bars for this timeframe.' : 'Chart history is still failing. No recovery has been confirmed; a restart has not been identified as the fix.')
    } finally { setCheckingChart(false) }
  }
  const [message, setMessage] = useState(null)
  const [recoveryPhase, setRecoveryPhase] = useState(null)

  useEffect(() => {
    refresh().catch(error => setMessage({ type: 'error', text: error.message }))
  }, [refresh])

  useEffect(() => {
    if (!recoveryPhase && status?.operational !== false) return undefined

    const timer = window.setInterval(() => {
      refresh()
        .then(result => {
          if (result.operational && recoveryPhase) {
            setRecoveryPhase(null)
            setMessage({ type: 'success', text: 'IBKR connection restored. Chart delivery still needs a successful chart request.' })
          }
        })
        .catch(error => setMessage({ type: 'error', text: error.message }))
    }, 3000)
    return () => window.clearInterval(timer)
  }, [recoveryPhase, refresh, status?.operational])

  const refreshStatus = async () => {
    setMessage(null)
    try {
      const result = await refresh()
      if (result.operational && recoveryPhase) {
        setRecoveryPhase(null)
        setMessage({ type: 'success', text: 'IBKR connection restored. Chart delivery still needs a successful chart request.' })
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.message })
    }
  }

  const run = async (action) => {
    if (
      action === 'restart-ib-gateway'
      && !window.confirm('Restart IB Gateway? IBKR may require a new 2FA approval before data resumes.')
    ) return
    setMessage(null)
    try {
      await restart(action)
      if (action === 'restart-ib-gateway') setRecoveryPhase('gateway-authentication')
      setMessage({
        type: 'success',
        text: action === 'restart-ib-gateway'
          ? 'IB Gateway restart accepted. Approve IBKR 2FA; Erevis will update automatically when data reconnects.'
          : 'IBKR Data Service restart request was accepted.',
      })
      await refresh().catch(() => null)
    } catch (error) {
      setMessage({ type: 'error', text: error.message })
    }
  }

  const connected = status?.operational === true && !recoveryPhase
  const healthy = connected && !chartFailure
  const gatewayRecommended = status?.recommendedAction === 'restart-ib-gateway' && !recoveryPhase
  const statusMessage = recoveryPhase === 'gateway-authentication' && !healthy
    ? 'Waiting for IB Gateway authentication. Approve IBKR 2FA if prompted; Erevis will update automatically.'
    : chartFailure ? chartStatus.message : connected ? 'IBKR connection is available. This check does not verify chart history or quote freshness.' : status?.message
  const diagnostics = status?.diagnostics
  const gatewayConfiguration = diagnostics?.gatewayConfiguration
  const classification = diagnostics?.classification
  const badge = connected && diagnostics?.lastIncident && !diagnostics?.activeIncident
    ? { label: 'Past connection incident', className: 'border-border bg-canvas2 text-inkMuted' }
    : diagnosticBadge(classification)
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-canvas overflow-hidden">
        <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div>
            <h2 className="font-black text-sm uppercase tracking-wide text-ink">Market Data Operations</h2>
            <p className="mt-0.5 font-mono text-xs text-inkMuted">IBKR Gateway and market-data-service recovery</p>
          </div>
          <button
            type="button"
            aria-label={refreshing ? 'Refreshing status' : 'Refresh status'}
            disabled={refreshing}
            onClick={refreshStatus}
            className="flex items-center gap-1.5 rounded-lg border border-border bg-canvas2 px-3 py-2 text-xs font-semibold text-ink disabled:opacity-60"
          >
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} /> {refreshing ? 'Refreshing…' : 'Refresh Status'}
          </button>
        </div>

        <div className="space-y-4 px-4 py-4">
          <div className={`flex items-start gap-2 rounded-lg border px-3 py-3 ${
            healthy ? 'border-forest/30 bg-forest/10 text-forest' : 'border-ember/30 bg-ember/10 text-ember'
          }`}>
            {healthy ? <CheckCircle size={16} /> : <AlertTriangle size={16} />}
            <div>
              <div className="text-sm font-semibold">{status ? statusMessage : 'Checking market-data status…'}</div>
              {status?.state && <div className="mt-1 font-mono text-[10px] uppercase">Provider state: {status.state}</div>}
            </div>
          </div>

          {chartFailure && chartStatus.request && <button type="button" disabled={checkingChart} onClick={checkChart}
            className="rounded-lg border border-border bg-canvas2 px-3 py-2 text-xs font-semibold text-ink disabled:opacity-60">
            {checkingChart ? 'Checking chart data…' : `Retry ${chartStatus.symbol} chart data`}
          </button>}
          {chartCheckMessage && <p role="status" className="text-sm text-ink">{chartCheckMessage}</p>}

          {gatewayConfiguration && <section className="overflow-hidden rounded-lg border border-border bg-canvas2/40">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2.5">
              <div>
                <h3 className="text-sm font-semibold text-ink">IB Gateway Console</h3>
                <p className="mt-0.5 font-mono text-[10px] text-inkMuted">Read-only Production session and deployment configuration</p>
              </div>
              <span className={`rounded-md border px-2 py-1 font-mono text-[10px] font-semibold uppercase ${
                healthy ? 'border-forest/30 bg-forest/10 text-forest' : 'border-ember/30 bg-ember/10 text-ember'
              }`}>
                {gatewayConnectionLabel(status?.state, recoveryPhase)}
              </span>
            </div>

            <div className="space-y-3 p-3">
              <dl className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                <GatewaySetting label="Trading mode" value={String(displayValue(gatewayConfiguration.tradingMode)).toUpperCase() === 'LIVE' ? 'Live' : gatewayConfiguration.tradingMode} />
                <GatewaySetting label="API port" value={gatewayConfiguration.apiPort} />
                <GatewaySetting label="API client ID" value={gatewayConfiguration.clientId} />
                <GatewaySetting label="Read-only API" setting="read-only-api" value={gatewayConfiguration.readOnlyApi == null ? null : gatewayConfiguration.readOnlyApi ? 'Enabled' : 'Disabled'} />
                <GatewaySetting label="Incoming API" value={gatewayConfiguration.incomingConnections === 'accept' ? 'Accepted' : gatewayConfiguration.incomingConnections} />
                <GatewaySetting label="Automatic restart" value={formatRestartTime(gatewayConfiguration.autoRestartTime, gatewayConfiguration.timezone)} />
                <GatewaySetting label="2FA timeout" value={gatewayConfiguration.twoFactorTimeoutAction === 'restart' ? 'Restart Gateway' : gatewayConfiguration.twoFactorTimeoutAction} />
                <GatewaySetting label="Relogin after timeout" value={gatewayConfiguration.reloginAfterTwoFactorTimeout == null ? null : gatewayConfiguration.reloginAfterTwoFactorTimeout ? 'Enabled' : 'Disabled'} />
                <GatewaySetting label="Java memory" value={Number.isFinite(gatewayConfiguration.javaHeapSizeMb) ? `${gatewayConfiguration.javaHeapSizeMb.toLocaleString()} MB` : null} />
                <GatewaySetting label="Service autoconnect" value={gatewayConfiguration.providerAutoconnect == null ? null : gatewayConfiguration.providerAutoconnect ? 'Enabled' : 'Disabled'} />
              </dl>
              <p className="font-mono text-[10px] text-inkMuted">
                This is an allowlisted read-only deployment configuration. Credentials, account data, and raw Gateway files are never returned.
              </p>
            </div>
          </section>}

          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-border bg-canvas2/40 p-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-ink"><Server size={15} /> IBKR Data Service</div>
              <p className="mt-1.5 font-mono text-xs text-inkMuted">A failed chart request alone does not identify a restart as the fix. Review the failure before restarting this shared service.</p>
              <button
                type="button"
                disabled={Boolean(busyAction)}
                onClick={() => run('restart-chart-service')}
                className="mt-3 rounded-lg bg-ink px-3 py-2 text-xs font-bold text-canvas disabled:opacity-50"
              >
                {busyAction === 'restart-chart-service' ? 'Verifying Passkey…' : 'Restart IBKR Data Service'}
              </button>
            </div>

            <div className={`rounded-lg border bg-canvas2/40 p-3 ${gatewayRecommended ? 'border-ember/60 ring-1 ring-ember/20' : 'border-border'}`}>
              <div className="flex items-center gap-2 text-sm font-semibold text-ink">
                <Server size={15} /> IB Gateway
                {gatewayRecommended && <span className="rounded bg-ember/10 px-1.5 py-0.5 font-mono text-[9px] uppercase text-ember">Recommended</span>}
              </div>
              <p className="mt-1.5 font-mono text-xs text-inkMuted">Restarting may require you to approve a new IBKR 2FA login before data resumes.</p>
              <button
                type="button"
                disabled={Boolean(busyAction)}
                onClick={() => run('restart-ib-gateway')}
                className="mt-3 rounded-lg border border-ember/50 bg-ember/10 px-3 py-2 text-xs font-bold text-ember disabled:opacity-50"
              >
                {busyAction === 'restart-ib-gateway'
                  ? 'Verifying Passkey…'
                  : gatewayRecommended ? 'Restart IB Gateway to Reconnect' : 'Restart IB Gateway'}
              </button>
            </div>
          </div>

          {diagnostics && <section className="overflow-hidden rounded-lg border border-border bg-canvas2/40">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2.5">
              <div>
                <h3 className="text-sm font-semibold text-ink">Connection Diagnostics</h3>
                <p className="mt-0.5 font-mono text-[10px] text-inkMuted">Redacted IB Gateway and market-data connection events</p>
              </div>
              <span className={`rounded-md border px-2 py-1 font-mono text-[10px] font-semibold uppercase ${badge.className}`}>
                {badge.label}
              </span>
            </div>

            <div className="space-y-3 p-3">
              <div>
                <p className="text-sm font-semibold text-ink">
                  {classification?.summary || 'Connection diagnostics have not loaded yet.'}
                </p>
                {classification?.startedAt && (
                  <p className="mt-1 font-mono text-[10px] text-inkMuted">
                    Incident began {formatDiagnosticTime(classification.startedAt)}
                    {!diagnostics.activeIncident && diagnostics.lastIncident?.endedAt && ` · Connection restored ${formatDiagnosticTime(diagnostics.lastIncident.endedAt)}. This past incident does not establish the cause of a current chart failure.`}
                  </p>
                )}
              </div>

              {diagnostics?.events?.length ? (
                <ol aria-label="Recent connection log" className="divide-y divide-border overflow-hidden rounded-md border border-border bg-canvas">
                  {diagnostics.events.slice(0, 20).map(event => (
                    <li key={event.id || `${event.event}-${event.at}`} className="grid gap-1 px-3 py-2.5 md:grid-cols-[9rem_7rem_1fr] md:gap-3">
                      <time className="font-mono text-[10px] text-inkMuted" dateTime={event.at}>
                        {formatDiagnosticTime(event.at)}
                      </time>
                      <span className="font-mono text-[10px] font-semibold uppercase text-inkMuted">
                        {event.component || 'Market Data'}
                      </span>
                      <div className="text-xs text-ink">
                        {event.message}
                        {Number(event.occurrences) > 1 && (
                          <span className="ml-1.5 font-mono text-[10px] text-inkMuted">
                            {Number(event.occurrences).toLocaleString()} occurrences
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="rounded-md border border-dashed border-border px-3 py-4 text-center font-mono text-xs text-inkMuted">
                  No connection events recorded since the service started.
                </div>
              )}
            </div>
          </section>}

          <p className="font-mono text-[10px] text-inkMuted">Every restart requires a fresh passkey verification. No broker orders or account settings are changed.</p>
          {message && (
            <div className={`font-mono text-xs ${message.type === 'success' ? 'text-forest' : 'text-ember'}`}>{message.text}</div>
          )}
        </div>
      </div>
    </div>
  )
}
